import { useState, type ReactNode } from 'react';
import { ChevronDown, FolderTree } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

interface Props {
  /** Wechselt, wenn navigiert wurde (Ordnerpfad oder Papierkorb) → klappt mobil wieder zu. */
  currentKey: string;
  children: ReactNode;
}

/**
 * Ordner-Rail: ab `lg` eine persistente Spalte, darunter eine Disclosure.
 *
 * FRONTEND_GUIDELINES §9: Desktop-Rails werden unter dem Breakpoint Drawer, Overflow oder
 * Bottom-Sheet — nicht gestapelt. Gestapelt stand der komplette Ordnerbaum über der
 * Inhaltsliste, die dieselben Ordner noch einmal zeigt: der Nutzer scrollt auf dem Weg zu
 * seiner Datei zweimal an derselben Information vorbei.
 *
 * Der Baum wird genau einmal gerendert; nur sein Container wechselt. Zwei Instanzen wären zwei
 * `nav`-Landmarken mit demselben Namen und doppelte Tastaturwege.
 */
export function AkteFolderRail({ currentKey, children }: Props) {
  const [open, setOpen] = useState(false);
  const [lastKey, setLastKey] = useState(currentKey);

  // Nach der Auswahl schließt die Disclosure, sonst verdeckt der Baum sein eigenes Ergebnis.
  if (lastKey !== currentKey) {
    setLastKey(currentKey);
    setOpen(false);
  }

  return (
    <div className="lg:sticky lg:top-4 lg:max-h-[calc(var(--shell-viewport,100dvh)-6rem)] lg:w-80 lg:shrink-0 lg:overflow-y-auto">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="akte-ordner-rail"
        className="border-line bg-card hover:bg-card-alt/60 flex min-h-11 w-full items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-left transition-colors lg:hidden"
      >
        <FolderTree size={17} className="text-ink-soft shrink-0" />
        {/* Wo der Nutzer steht, sagt der Breadcrumb direkt darunter — hier steht deshalb die
            Handlung, nicht noch einmal derselbe Ordnername. */}
        <span className="text-ink min-w-0 flex-1 truncate font-serif font-medium">
          Ordner wechseln
        </span>
        <ChevronDown
          size={16}
          className={cn('text-ink-faint shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>

      <div
        id="akte-ordner-rail"
        className={cn(
          'lg:mt-0 lg:block lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0',
          open ? 'border-line bg-card mt-2 rounded-lg border p-2' : 'hidden',
        )}
      >
        {children}
      </div>
    </div>
  );
}
